import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, ComposedChart, Area } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MonthlyPerformance = () => {
  const [performanceData, setPerformanceData] = useState([]);
  const [quotesData, setQuotesData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [showMonthDetail, setShowMonthDetail] = useState(false);

  useEffect(() => {
    loadPerformance();
    loadQuotesData();
  }, [selectedYear]);

  const loadPerformance = async () => {
    try {
      const response = await axios.get(`${API}/performance/monthly?year=${selectedYear}`);
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Failed to load performance data', error);
    } finally {
      setLoading(false);
    }
  };

  const loadQuotesData = async () => {
    try {
      const response = await axios.get(`${API}/quotes`);
      const quotes = response.data;
      
      // Group quotes by month for the selected year
      const monthlyQuotes = {};
      for (let i = 1; i <= 12; i++) {
        monthlyQuotes[i] = { total_value: 0, count: 0 };
      }
      
      quotes.forEach(quote => {
        const quoteDate = new Date(quote.date);
        if (quoteDate.getFullYear() === selectedYear) {
          const month = quoteDate.getMonth() + 1;
          monthlyQuotes[month].total_value += quote.total || 0;
          monthlyQuotes[month].count += 1;
        }
      });
      
      setQuotesData(monthlyQuotes);
    } catch (error) {
      console.error('Failed to load quotes data', error);
    }
  };

  const calculateAnnualSummary = () => {
    const perfSummary = performanceData.reduce(
      (acc, month) => ({
        revenue: acc.revenue + (month.total_revenue || 0),
        labour_hours: acc.labour_hours + (month.total_labour_hours || 0),
        material_cost: acc.material_cost + (month.total_material_cost || 0),
        gross_margin: acc.gross_margin + (month.gross_margin || 0),
        projects: acc.projects + (month.projects_completed || 0),
        burn_rate: acc.burn_rate + (month.burn_rate || 0),
      }),
      { revenue: 0, labour_hours: 0, material_cost: 0, gross_margin: 0, projects: 0, burn_rate: 0 }
    );
    
    // Calculate total quote value for the year
    const quoteValue = Object.values(quotesData).reduce((sum, m) => sum + m.total_value, 0);
    
    return { ...perfSummary, quoteValue };
  };

  // Prepare combined data for the impact chart
  const prepareCombinedData = () => {
    return performanceData.map((month, index) => {
      const monthNum = index + 1;
      const quoteData = quotesData[monthNum] || { total_value: 0, count: 0 };
      
      return {
        month: month.month,
        quote_value: quoteData.total_value,
        revenue: month.total_revenue,
        burn_rate: month.burn_rate || 0,
        net_position: month.total_revenue - (month.burn_rate || 0),
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-primary text-xl font-mono uppercase tracking-widest">Loading...</div>
      </div>
    );
  }

  const annualSummary = calculateAnnualSummary();
  const grossMargin = annualSummary.gross_margin;
  const marginPercentage = annualSummary.revenue > 0 ? (grossMargin / annualSummary.revenue) * 100 : 0;
  const combinedData = prepareCombinedData();
  
  const hasRevenueData = annualSummary.revenue > 0 || annualSummary.projects > 0;
  
  // Generate year options dynamically
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = currentYear - 5; i <= currentYear + 1; i++) {
    yearOptions.push(i);
  }

  return (
    <div className="p-8" data-testid="monthly-performance-page">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-foreground mb-2" data-testid="performance-title">
              PERFORMANCE ANALYTICS
            </h1>
            <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground">
              Financial analysis • Year: {selectedYear}
            </p>
          </div>
          <select
            data-testid="year-select"
            className="px-4 py-2 bg-background border border-input rounded-sm font-mono text-sm"
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Data Status Banner */}
      {!hasRevenueData && (
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
          <p className="text-sm font-mono text-yellow-600">
            ℹ️ No revenue data found for {selectedYear}. Revenue is calculated from projects with invoice dates. 
            Quotes are shown in "Total Quote Value" but don't count as revenue until converted to invoiced projects.
          </p>
        </div>
      )}

      {/* Annual Summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Total Quote Value
            </p>
            <p className="text-2xl font-bold font-mono text-blue-500 break-all">
              ${annualSummary.quoteValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Potential Pipeline</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Annual Revenue
            </p>
            <p className="text-2xl font-bold font-mono text-primary break-all">
              ${annualSummary.revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Invoiced Projects</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Total Burn Rate
            </p>
            <p className="text-2xl font-bold font-mono text-red-500 break-all">
              ${annualSummary.burn_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Operating Costs</p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Gross Margin
            </p>
            <p className="text-2xl font-bold font-mono text-green-500 break-all">
              ${grossMargin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p className="text-sm font-mono text-muted-foreground mt-1">
              {marginPercentage.toFixed(1)}% margin
            </p>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">
              Projects Completed
            </p>
            <p className="text-2xl font-bold font-mono text-foreground">
              {annualSummary.projects}
            </p>
            <p className="text-xs text-muted-foreground mt-2">Delivered</p>
          </CardContent>
        </Card>
      </div>

      {/* Financial Impact Chart - Quote Value vs Revenue vs Burn Rate */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
            Financial Impact Analysis
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-2">
            Track the relationship between Quote Value (potential), Revenue (actual), and Burn Rate (costs)
          </p>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart data={combinedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" style={{ fontSize: '12px' }} />
              <YAxis stroke="#a1a1aa" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  border: '1px solid #27272a',
                  borderRadius: '2px',
                  fontFamily: 'JetBrains Mono',
                  fontSize: '12px',
                }}
                formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
              <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              
              {/* Area for burn rate (background context) */}
              <Area 
                type="monotone" 
                dataKey="burn_rate" 
                fill="#ef4444" 
                fillOpacity={0.1}
                stroke="none"
                name="Burn Rate Zone" 
              />
              
              {/* Bars for comparison */}
              <Bar dataKey="quote_value" fill="#3b82f6" name="Quote Value" />
              <Bar dataKey="revenue" fill="#f97316" name="Revenue" />
              
              {/* Lines for trends */}
              <Line 
                type="monotone" 
                dataKey="burn_rate" 
                stroke="#ef4444" 
                strokeWidth={2} 
                dot={{ r: 4 }}
                name="Burn Rate" 
              />
              <Line 
                type="monotone" 
                dataKey="net_position" 
                stroke="#22c55e" 
                strokeWidth={2} 
                strokeDasharray="5 5"
                dot={{ r: 4 }}
                name="Net Position (Revenue - Burn)" 
              />
            </ComposedChart>
          </ResponsiveContainer>
          
          {/* Key Insights */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm">
              <p className="text-xs font-mono uppercase tracking-widest text-blue-400 mb-2">Quote Value</p>
              <p className="text-sm text-muted-foreground">
                Total value of quotes generated. Shows sales pipeline potential.
              </p>
            </div>
            <div className="p-4 bg-primary/10 border border-primary/20 rounded-sm">
              <p className="text-xs font-mono uppercase tracking-widest text-primary mb-2">Revenue</p>
              <p className="text-sm text-muted-foreground">
                Actual income from completed and invoiced projects.
              </p>
            </div>
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-sm">
              <p className="text-xs font-mono uppercase tracking-widest text-red-400 mb-2">Burn Rate</p>
              <p className="text-sm text-muted-foreground">
                Monthly operational costs. When Revenue exceeds Burn Rate, you're profitable.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Revenue vs Burn Rate Comparison */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
            Revenue vs Burn Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" style={{ fontSize: '12px' }} />
              <YAxis stroke="#a1a1aa" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  border: '1px solid #27272a',
                  borderRadius: '2px',
                  fontFamily: 'JetBrains Mono',
                  fontSize: '12px',
                }}
                formatter={(value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              />
              <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              <Bar dataKey="total_revenue" fill="#f97316" name="Revenue" />
              <Bar dataKey="burn_rate" fill="#ef4444" name="Burn Rate" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Margin Chart */}
      <Card className="border-border mb-6">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
            Margin Percentage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={performanceData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="month" stroke="#a1a1aa" style={{ fontSize: '12px' }} />
              <YAxis stroke="#a1a1aa" style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#18181b',
                  border: '1px solid #27272a',
                  borderRadius: '2px',
                  fontFamily: 'JetBrains Mono',
                  fontSize: '12px',
                }}
              />
              <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }} />
              <Line type="monotone" dataKey="margin_percentage" stroke="#22c55e" strokeWidth={2} name="Margin %" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Monthly Table with Pipeline Metrics */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-xl font-semibold tracking-wide uppercase text-muted-foreground">
            Monthly Breakdown & Pipeline
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-2">Click any month to see detailed pipeline metrics</p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Month</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Quotes</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Invoices</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Projects</th>
                  <th className="text-right py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Revenue</th>
                  <th className="text-right py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Net</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Conv %</th>
                  <th className="text-center py-3 px-2 font-mono uppercase text-xs tracking-wider text-muted-foreground">Avg Days</th>
                </tr>
              </thead>
              <tbody>
                {performanceData.map((month, index) => {
                  const quotesMonth = quotesData[index + 1] || { total_value: 0 };
                  const isProfit = month.total_revenue - month.burn_rate > 0;
                  const hasActivity = month.quotes_count > 0 || month.invoices_count > 0;
                  
                  return (
                    <tr 
                      key={month.month} 
                      className={`border-b border-border transition-colors ${hasActivity ? 'hover:bg-primary/10 cursor-pointer' : 'opacity-50'}`}
                      onClick={() => hasActivity && (setSelectedMonth(month), setShowMonthDetail(true))}
                    >
                      <td className="py-3 px-2 font-medium">{month.month}</td>
                      <td className="py-3 px-2 text-center font-mono">
                        <span className={`px-2 py-1 rounded ${month.quotes_count > 0 ? 'bg-blue-500/20 text-blue-400' : 'text-muted-foreground'}`}>
                          {month.quotes_count}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center font-mono">
                        <span className={`px-2 py-1 rounded ${month.invoices_count > 0 ? 'bg-primary/20 text-primary' : 'text-muted-foreground'}`}>
                          {month.invoices_count}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center font-mono">
                        <span className={`px-2 py-1 rounded ${month.projects_count > 0 ? 'bg-green-500/20 text-green-400' : 'text-muted-foreground'}`}>
                          {month.projects_count}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right font-mono text-primary">
                        ${month.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td className={`py-3 px-2 text-right font-mono font-bold ${isProfit ? 'text-green-500' : 'text-red-500'}`}>
                        ${(month.total_revenue - month.burn_rate).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </td>
                      <td className="py-3 px-2 text-center font-mono">
                        {month.pipeline_conversion_rate !== null ? (
                          <span className={`px-2 py-1 rounded ${month.pipeline_conversion_rate >= 50 ? 'bg-green-500/20 text-green-400' : month.pipeline_conversion_rate >= 25 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                            {month.pipeline_conversion_rate.toFixed(0)}%
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-3 px-2 text-center font-mono text-xs">
                        {month.avg_quote_to_invoice_days !== null ? (
                          <span className="text-muted-foreground">
                            {month.avg_quote_to_invoice_days.toFixed(0)}d
                          </span>
                        ) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Month Detail Modal */}
      {showMonthDetail && selectedMonth && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowMonthDetail(false)}>
          <div className="bg-card border border-border rounded-sm max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-3xl font-black tracking-tight uppercase text-foreground">{selectedMonth.month} {selectedMonth.year}</h2>
                  <p className="text-sm font-mono uppercase tracking-wide text-muted-foreground mt-1">Pipeline & Performance Details</p>
                </div>
                <button 
                  onClick={() => setShowMonthDetail(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Pipeline Overview Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Quotes</p>
                    <p className="text-2xl font-bold font-mono text-blue-500">{selectedMonth.quotes_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">Generated</p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Invoices</p>
                    <p className="text-2xl font-bold font-mono text-primary">{selectedMonth.invoices_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">Created</p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Projects</p>
                    <p className="text-2xl font-bold font-mono text-green-500">{selectedMonth.projects_count}</p>
                    <p className="text-xs text-muted-foreground mt-1">Active</p>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4">
                    <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground mb-1">Revenue</p>
                    <p className="text-2xl font-bold font-mono text-primary">${selectedMonth.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</p>
                    <p className="text-xs text-muted-foreground mt-1">Collected</p>
                  </CardContent>
                </Card>
              </div>

              {/* Pipeline Metrics */}
              <Card className="border-border mb-6">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold uppercase">Pipeline Performance</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <span className="text-sm font-mono uppercase tracking-wide text-muted-foreground">Conversion Rate</span>
                    <div className="text-right">
                      {selectedMonth.pipeline_conversion_rate !== null ? (
                        <>
                          <span className={`text-xl font-bold font-mono ${selectedMonth.pipeline_conversion_rate >= 50 ? 'text-green-500' : selectedMonth.pipeline_conversion_rate >= 25 ? 'text-yellow-500' : 'text-red-500'}`}>
                            {selectedMonth.pipeline_conversion_rate.toFixed(1)}%
                          </span>
                          <p className="text-xs text-muted-foreground">
                            {selectedMonth.invoices_count} of {selectedMonth.quotes_count} quotes converted
                          </p>
                        </>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between py-3 border-b border-border">
                    <span className="text-sm font-mono uppercase tracking-wide text-muted-foreground">Avg. Quote → Invoice</span>
                    <div className="text-right">
                      {selectedMonth.avg_quote_to_invoice_days !== null ? (
                        <>
                          <span className="text-xl font-bold font-mono text-primary">
                            {selectedMonth.avg_quote_to_invoice_days.toFixed(0)} days
                          </span>
                          <p className="text-xs text-muted-foreground">Time to conversion</p>
                        </>
                      ) : (
                        <span className="text-muted-foreground">N/A</span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <span className="text-sm font-mono uppercase tracking-wide text-muted-foreground">Net Position</span>
                    <div className="text-right">
                      <span className={`text-xl font-bold font-mono ${selectedMonth.total_revenue - selectedMonth.burn_rate >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        ${(selectedMonth.total_revenue - selectedMonth.burn_rate).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <p className="text-xs text-muted-foreground">Revenue - Burn Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Financial Details */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold uppercase">Financial Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Revenue (Invoiced)</span>
                    <span className="font-mono text-primary">${selectedMonth.total_revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Material Costs</span>
                    <span className="font-mono">${selectedMonth.total_material_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Gross Margin</span>
                    <span className="font-mono text-green-500">${selectedMonth.gross_margin.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({selectedMonth.margin_percentage.toFixed(1)}%)</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Burn Rate</span>
                    <span className="font-mono text-red-500">${selectedMonth.burn_rate.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-3 border-t border-border">
                    <span className="text-muted-foreground font-bold">Labour Hours</span>
                    <span className="font-mono font-bold">{selectedMonth.total_labour_hours.toFixed(1)}h</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonthlyPerformance;
